import { ParamMap } from '@angular/router';
import moment from 'moment';
import { FilterMetadata, SelectItem } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';

export interface FilterParam {
    filters?: Filter[];
    and?: boolean;
    not?: boolean;
    comparator?: string;
    property?: string;
    value?: any;
}

export class Filter {
    static readonly URL_KEY: string = '$filter=';
    static readonly COMPARATOR = {
        // Value comparators
        EQ: 'eq',
        NE: 'ne',
        GE: 'ge',
        GT: 'gt',
        LE: 'le',
        LT: 'lt',
        // String functions
        Contains: 'substringof',
        EndsWith: 'endswith',
        StartsWith: 'startswith'
    };
    static readonly OPERATOR = {
        AND: ' and ',
        NOT: 'not ',
        OR: ' or '
    };
    static readonly SEPARATOR: string = ',';

    // Comparateur autorisés selon type de donnée
    static readonly TextComparators: SelectItem[] = [
        {
            label: 'Contient',
            value: Filter.COMPARATOR.Contains,
        },
        {
            label: 'Commence par',
            value: Filter.COMPARATOR.StartsWith,
        },
        {
            label: 'Finit par',
            value: Filter.COMPARATOR.EndsWith,
        },
        {
            label: 'Est égal à',
            value: Filter.COMPARATOR.EQ,
        },
    ]
    static readonly NumericComparators: SelectItem[] = [
        {
            label: 'Est égal à',
            value: Filter.COMPARATOR.EQ,
        },
        {
            label: 'Est inférieur ou égal à',
            value: Filter.COMPARATOR.LE,
        },
        {
            label: 'Est supérieur ou égal à',
            value: Filter.COMPARATOR.GE,
        },
    ]
    static readonly DateComparators: SelectItem[] = [
        {
            label: 'Est égal à',
            value: Filter.COMPARATOR.EQ,
        },
        {
            label: 'Est inférieure ou égal à',
            value: Filter.COMPARATOR.LE,
        },
        {
            label: 'Est supérieure ou égal à',
            value: Filter.COMPARATOR.GE,
        },
    ]

    /**
     * ATTRIBUTES
     */

    filters: Filter[];
    and: boolean;
    not: boolean;
    comparator: string;
    property: string;
    value: any;

    /**
     * MÉTHODES STATIQUES
     */

    static fromQueryParams(queryParams: ParamMap): Filter[] {
        let filters: Filter[] = [];

        if (!!queryParams.get('$filter')) {
            let rawFiltersString = queryParams.get('$filter')!;
            let rawFilters: string[] = rawFiltersString.split(Filter.SEPARATOR);

            for (let i = 0; i < rawFilters.length; i++) {
                let rawFilter = rawFilters[i].trim();
                filters.push(Filter.parseFilterString(rawFilter));
            }
        }

        return filters;
    }

    private static parseFilterString(filterString: string): Filter {
        filterString = filterString.trim();

        // Not operator
        let not = false;
        if (filterString.startsWith(Filter.OPERATOR.NOT)) {
            not = true;
            filterString = filterString.substring(Filter.OPERATOR.NOT.length).trim();
        }

        // Remove outer parentheses if they wrap the entire filter
        while (filterString.startsWith('(') && filterString.endsWith(')')) {
            // Check if these are really outer parentheses wrapping everything
            let innerFilter = filterString.substring(1, filterString.length - 1);
            let depth = 0;
            let isFullyWrapped = true;

            for (let i = 0; i < innerFilter.length; i++) {
                if (innerFilter[i] === '(') depth++;
                if (innerFilter[i] === ')') {
                    depth--;
                    if (depth < 0) {
                        isFullyWrapped = false;
                        break;
                    }
                }
            }

            if (isFullyWrapped && depth === 0) {
                filterString = innerFilter;
            } else {
                break;
            }
        }

        // Check for OR operator (highest priority)
        let orParts = Filter.splitByOperator(filterString, Filter.OPERATOR.OR);
        if (orParts.length > 1) {
            let subFilters: Filter[] = [];
            for (let part of orParts) {
                subFilters.push(Filter.parseFilterString(part));
            }
            return new Filter({
                filters: subFilters,
                and: false,
                not: not
            });
        }

        // Check for AND operator
        let andParts = Filter.splitByOperator(filterString, Filter.OPERATOR.AND);
        if (andParts.length > 1) {
            let subFilters: Filter[] = [];
            for (let part of andParts) {
                subFilters.push(Filter.parseFilterString(part));
            }
            return new Filter({
                filters: subFilters,
                and: true,
                not: not
            });
        }

        // Simple filter
        let filterParts = filterString.match(/([\w\/\.]+)\s(eq|ne|gt|ge|lt|le|substringof|startswith|endswith)\s(.+)/);
        if (filterParts && filterParts.length === 4) {
            let property = filterParts[1].replace(/\//g, '.');
            let comparator = filterParts[2];
            let valueRaw = filterParts[3].trim();

            let value: any;
            if (valueRaw === 'null') {
                value = null;
            } else if (valueRaw.startsWith("'") && valueRaw.endsWith("'")) {
                value = valueRaw.substring(1, valueRaw.length - 1);
            } else if (valueRaw === 'true' || valueRaw === 'false') {
                value = (valueRaw === 'true');
            } else if (!isNaN(Number(valueRaw))) {
                value = Number(valueRaw);
            } else if (moment(valueRaw, 'YYYY-MM-DD', true).isValid()) {
                value = moment(valueRaw, 'YYYY-MM-DD').toDate();
            } else {
                throw 'Invalid filter value in query params!';
            }

            return new Filter({
                not: not,
                property: property,
                comparator: comparator,
                value: value
            });
        }

        throw 'Invalid filter format in query params!';
    }

    private static splitByOperator(filterString: string, operator: string): string[] {
        let parts: string[] = [];
        let currentPart = '';
        let parenthesesLevel = 0;
        let i = 0;

        while (i < filterString.length) {
            let char = filterString[i];

            if (char === '(') {
                parenthesesLevel++;
                currentPart += char;
            } else if (char === ')') {
                parenthesesLevel--;
                currentPart += char;
            } else if (parenthesesLevel === 0 && filterString.substring(i, i + operator.length) === operator) {
                // Found operator at level 0
                parts.push(currentPart.trim());
                currentPart = '';
                i += operator.length - 1; // -1 because we increment at the end of loop
            } else {
                currentPart += char;
            }

            i++;
        }

        // Add last part
        if (currentPart.trim().length > 0) {
            parts.push(currentPart.trim());
        }

        return parts.length > 1 ? parts : [filterString];
    }

    static toUrlString(filters: Filter[], withPrefix: boolean = true): string {
        let url = '';
        if (withPrefix) {
            // Init URL with keyword
            url = Filter.URL_KEY;
        }

        // Concatenate filters
        for (let key in filters) {
            url += filters[key].toString();
            url += Filter.SEPARATOR;
        }
        // Remove last separator
        url = url.substring(0, url.length - 1);

        return url;
    }

    /**
     * MÉTHODES D'INSTANCE
     */

    constructor(param: FilterParam) {
        this.filters = typeof param.filters !== 'undefined' ? param.filters : [];
        this.and = typeof param.and !== 'undefined' ? param.and : true;
        this.not = typeof param.not !== 'undefined' ? param.not : false;
        this.comparator = typeof param.comparator !== 'undefined' ? param.comparator : Filter.COMPARATOR.EQ;
        this.property = typeof param.property !== 'undefined' ? param.property : '';
        this.value = typeof param.value !== 'undefined' ? param.value : null;
    }

    public toString(): string {
        let url: string = '';

        // Not operator
        url += this.not ? Filter.OPERATOR.NOT : '';

        // Simple Filter or array of Filters ?
        if (typeof this === 'string') {
            return this;
        } else if (this.filters.length > 0) {
            url += '(';

            for (let i = 0; i < this.filters.length; i++) {
                // Recursive call
                url += this.filters[i].toString();

                // Logical operator
                if (i < this.filters.length - 1) {
                    url += this.and ? Filter.OPERATOR.AND : Filter.OPERATOR.OR;
                }
            }

            url += ')';
        } else {
            let value: string;
            if (this.value === null || typeof this.value === 'undefined') {
                value = 'null';
            } else {
                switch (typeof this.value) {
                    case 'boolean':
                        value = this.value.toString();
                        break;
                    case 'number':
                        if (this.property.endsWith('.month')) {
                            // Special handling for month as JS starts with index 0
                            let month = this.value + 1;
                            value = month.toString();
                        } else {
                            value = this.value.toString();
                        }
                        break;
                    case 'string':
                        value = "'" + this.value + "'";
                        break;
                    case 'object':
                        if (this.value instanceof Date) {
                            this.value = moment(this.value);
                        }
                        if (moment.isMoment(this.value)) {
                            value = "'" + this.value.format('YYYY-MM-DD') + "'";
                        } else {
                            throw 'Invalid filter value!';
                        }
                        break;
                    default:
                        throw 'Invalid filter value!';
                }
            }
            url += this.property.replace(/\./g, '/') + ' ' + this.comparator + ' ' + value;
        }

        return url;
    }
}
